
// Import necessary modules from Three.js and custom utilities
import * as THREE from 'three';
import { OrbitControls } from "../Utils/OrbitControls";
import { LABELS } from "../Global/variables";
import { CSS2DRenderer } from "three/examples/jsm/renderers/CSS2DRenderer";
import { LINES, OBSTACLE } from "../Global/materials";
import { createLabelDiv } from "../Global/functions";
import { initSmartGuides } from '../Segments/Snaps/GuidingLines';
import { updateLineResolutions } from "../Global/fatLines";
import { applyCameraKeyBindings } from "../Controls/CameraKeyBindings";

// SceneManager class (singleton)
class SceneManager {
    constructor() {
        // Singleton pattern: ensure only one instance of SceneManager exists
        if (!SceneManager.instance) {
            this.initialize();
            SceneManager.instance = this;
        }
        return SceneManager.instance;
    }

    // Initializes the scene, camera, renderer, lights, and controls
    initialize() {
        // Create a new Three.js scene
        this.scene = new THREE.Scene();

        // Render container (assigned later by Main)
        this.renderContainer = null;

        // Set scene height considering a UI offset
        const { width, height, headerOffset } = this.getRenderSize();
        this.headerOffset = headerOffset;
        this.height = height;

        // Setup perspective camera
        this.camera = new THREE.PerspectiveCamera(
            45,
            width / this.height,
            0.1,
            5000
        );
        this.camera.up.set(0, 0, 1); // Z is up for this application

        // Create WebGL renderer
        this.renderer = new THREE.WebGLRenderer({ antialias: true });
        this.renderer.setClearColor(0x000000); // Background color
        this.renderer.setSize(width, this.height); // Size of the render area

        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap; // Optional: softer shadows

        // Initialize orbit controls for camera navigation
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.addEventListener('start', this.onStartCameraRotation.bind(this));
        this.controls.addEventListener('end', this.onEndCameraRotation.bind(this));

        // Setup lighting
        this.directionalLight = new THREE.DirectionalLight(0xffffff, 5);
        this.ambientLight = new THREE.AmbientLight(0xffffff, 0.8);

        // Will be initialized later
        this.labelRenderer = null;

        // Configure and populate the scene
        this.setUpScene();
    }

    getHeaderOffset() {
        if (typeof window === "undefined") return 0;
        const styles = getComputedStyle(document.documentElement);
        const scaledValue = styles.getPropertyValue("--app-header-height-scaled").trim();
        const baseValue = styles.getPropertyValue("--app-header-height").trim();
        const parsedScaled = parseFloat(scaledValue);
        const parsedBase = parseFloat(baseValue);
        if (Number.isFinite(parsedScaled)) return parsedScaled;
        return Number.isFinite(parsedBase) ? parsedBase : 72;
    }

    getRenderSize() {
        const headerOffset = this.getHeaderOffset();
        if (this.renderContainer?.getBoundingClientRect) {
            const rect = this.renderContainer.getBoundingClientRect();
            const hasSize = rect.width > 0 && rect.height > 0;
            const width = hasSize ? rect.width : window.innerWidth;
            const height = hasSize ? rect.height : window.innerHeight - headerOffset;
            return { width, height, headerOffset, useContainer: hasSize };
        }
        return {
            width: window.innerWidth,
            height: window.innerHeight - headerOffset,
            headerOffset,
            useContainer: false,
        };
    }

    setRenderContainer(container) {
        this.renderContainer = container || null;
        this.handleResize();
    }

    // Set up the complete scene
    setUpScene() {
        this.setControl(); // Configure camera and control settings
        this.setUpLabelRenderer(); // Setup 2D label renderer
        this.setUpRefrenceAngle(); // Setup angle label
        this.setUpLengthLabel();
        this.setUpRefrenceLine(); // Add reference measurement line
        this.setDirectionalLights(); // Add main directional light
        this.setAmbientLight(); // Add ambient light
        initSmartGuides(this.scene)

        // Add geometric references to the scene
        this.scene.add(OBSTACLE.circle);
        this.scene.add(OBSTACLE.box);
    }

    // Configure camera controls
    setControl() {
        this.camera.position.set(0, 0, 50); // Initial camera position
        this.controls.listenToKeyEvents(window); // Listen to keyboard inputs

        // Keep primary click free for the editor while applying the user's
        // browser-persisted camera bindings to the camera controls.
        applyCameraKeyBindings(this.controls);

        this.controls.zoomToCursor = true;
        this.controls.rotateSpeed = 1;
        this.controls.enablePan = true;
        this.controls.maxPolarAngle = Math.PI / 2; // Limit vertical rotation to top view
        this.controls.target.set(0, 0, 0); // Look at center
        this.controls.update();

        // Optionally add axis helper
        // const axis = new THREE.AxesHelper(250);
        // this.scene.add(axis);
    }

    // Add directional light source to the scene
    setDirectionalLights() {
        this.directionalLight.position.set(50, 50, 50);
        this.directionalLight.castShadow = true;
        this.directionalLight.shadow.mapSize.width = 512;
        this.directionalLight.shadow.mapSize.height = 512;
        this.directionalLight.shadow.camera.near = 0.5;
        this.directionalLight.shadow.camera.far = 500;

        this.scene.add(this.directionalLight);
    }

    // Add ambient lighting to ensure scene visibility
    setAmbientLight() {
        this.scene.add(this.ambientLight);
    }

    // Set up CSS2D renderer for HTML-based labels
    setUpLabelRenderer() {
        this.labelRenderer = new CSS2DRenderer();
        const { width, height } = this.getRenderSize();
        this.labelRenderer.setSize(width, height);

        // Style the label renderer container
        this.labelRenderer.domElement.style.position = 'fixed';
        this.labelRenderer.domElement.style.top = `${this.headerOffset}px`;
        this.labelRenderer.domElement.style.left = '0px';
        this.labelRenderer.domElement.style.pointerEvents = 'none';
        this.labelRenderer.domElement.style.fontSize = '14px';
        this.labelRenderer.domElement.style.textShadow = '1px 1px 3px rgba(0, 0, 0, 0.8)';
        this.labelRenderer.domElement.style.transform = 'rotateX(0) rotateY(0) rotateZ(0)';
    }

    // Add a line to represent reference distance/measurement
    setUpRefrenceLine() {
        LINES.referenceLine.computeLineDistances();
        LINES.referenceLine.visible = false;
        this.scene.add(LINES.referenceLine);
    }

    // Add a label to show angle values
    setUpRefrenceAngle() {
        LABELS.angle = createLabelDiv("", "0°");
        LABELS.angle.visible = false;
        this.scene.add(LABELS.angle);
    }

    setUpLengthLabel() {
        LABELS.length = createLabelDiv("", "0.00 m");
        LABELS.length.visible = false;
        this.scene.add(LABELS.length);
    }

    // Handle window resize events to update camera and renderer
    handleResize() {
        const { width, height, headerOffset, useContainer } = this.getRenderSize();
        this.headerOffset = headerOffset;
        this.height = height;
        this.camera.aspect = width / this.height;
        this.camera.updateProjectionMatrix();

        // Resize both 3D and 2D renderers
        this.renderer.setSize(width, this.height);
        // Keep fat-line (segment boundary) materials matched to the new size.
        updateLineResolutions(this.scene, width, this.height);
        const originalMask = this.camera.layers.mask;
        const originalAutoClear = this.renderer.autoClear;
        try {
            this.camera.layers.set(0);
            this.renderer.render(this.scene, this.camera);
            this.renderer.clearDepth();
            this.camera.layers.set(1);
            this.renderer.autoClear = false;
            this.renderer.render(this.scene, this.camera);
        } finally {
            this.camera.layers.mask = originalMask;
            this.renderer.autoClear = originalAutoClear;
        }
        if (this.labelRenderer) {
            this.labelRenderer.setSize(width, this.height);
            this.labelRenderer.domElement.style.position = useContainer ? 'absolute' : 'fixed';
            this.labelRenderer.domElement.style.top = useContainer ? '0px' : `${this.headerOffset}px`;
            this.labelRenderer.domElement.style.left = '0px';
        }
    }

    // Camera control event handlers
    onStartCameraRotation() {
        this.isCameraRotating = true;
    }

    onEndCameraRotation() {
    this.isCameraRotating = false;
        //these below logs are just for testing purposes, feel free to remove later
   
}


    // Public method to access scene elements
    getSceneObjects() {
        return {
            scene: this.scene,
            camera: this.camera,
            renderer: this.renderer,
            controls: this.controls,
            labelRenderer: this.labelRenderer,
        };
    }
}

// Export a singleton instance of SceneManager
export const sceneManager = new SceneManager();
